/**
 * The recurrence schedule an invoice can carry, from the browser's side.
 *
 * Its own module rather than eight more fields and four more calls in
 * `sales.ts`, which is already at 418 lines and holds the four document kinds.
 * Recurrence is a second lifecycle laid over one of those kinds, with its own
 * three-valued rules about what NULL means, and it reads better beside them than
 * inside them.
 *
 * The type below is an EXTENSION rather than a change to `Invoice`. Callers that
 * do not care about recurrence - the editor, the PDF button, the payments card -
 * keep the type they already had, and the two screens that do care widen it at
 * the point of use. That also keeps this feature out of the one file every
 * screen in the section imports.
 */
import { api } from "./api.js";
import type { Invoice } from "./sales.js";

/** What the API adds to an invoice once recurrence exists. */
export interface RecurringFields {
  /** The period in months. NULL means this invoice does not recur. */
  recurringEveryMonths: number | null;
  /** When the next invoice is due. NULL means the series has finished. */
  recurringNextAt: string | null;
  recurringEndAt: string | null;
  recurringMaxCount: number | null;
  recurringCount: number;
  /** When one was last actually made. NULL means none yet. */
  recurringLastAt: string | null;
  /** On a generated invoice, the template that made it. */
  recurringParentId: string | null;
  recurringAutoSend: boolean;
}

export type RecurringInvoice = Invoice & RecurringFields;

/** One invoice a template has produced, as the children list returns it. */
export interface RecurringChild {
  id: string;
  number: string;
  status: string;
  issueDate: string;
  dueDate: string | null;
  totalCents: number;
  paidCents: number;
  currency: string;
  archivedAt: string | null;
}

/**
 * The whole schedule, always sent whole.
 *
 * `everyMonths: null` clears it. This is a PUT and not a patch on purpose: an
 * omitted key in a patch means "leave this alone", and a schedule needs a third
 * state. "The caller did not mention the end date" and "the caller wants no end
 * date" are the difference between a series that stops in December and one that
 * runs forever.
 */
export interface SchedulePut {
  everyMonths: number | null;
  nextAt?: string | null;
  endAt?: string | null;
  maxCount?: number | null;
  /**
   * ALWAYS send this from a form. Omitted means false on the server, which is
   * the PUT contract behaving correctly - but it means saving an end date on a
   * self-posting series would quietly turn the posting off, and nobody would
   * find out until an invoice failed to arrive.
   */
  autoSend?: boolean;
}

/**
 * A date-picker day (`YYYY-MM-DD`) as the instant the API stores it: midday UTC
 * on that same calendar day.
 *
 * NOT `toIso` from the projects helpers, which is what every other form in the
 * CRM uses. That one builds LOCAL midnight - `new Date(v + "T00:00:00")` - and
 * in Tallinn local midnight is 21:00 or 22:00 UTC on the PREVIOUS day. The API
 * pins a schedule to midday UTC by reading the UTC calendar day, so a picker
 * showing 30 September would be stored as the 29th, redrawn as the 29th, and
 * every invoice in the series would then fall a day earlier than the operator
 * set. Measured in the browser: the form sent `2026-09-29T21:00:00.000Z` for a
 * field reading 30.09.2026.
 *
 * Midday is the same convention the server uses, so this conversion and
 * `normalizeScheduleDate` agree instead of one correcting the other.
 */
export function toScheduleIso(day: string): string | null {
  if (!day) return null;
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0)).toISOString();
}

/** What became of the mail, when the template asked for one. NULL = it did not. */
export interface AutoSendOutcome {
  sent: boolean;
  /** `no_recipient`, `disabled`, `failed`, `mark_failed`, `dev`, or null. */
  reason: string | null;
}

export interface RunNowResult {
  childId: string;
  childNumber: string;
  nextAt: string | null;
  exhausted: boolean;
  /** Whether the template asked for the child to be posted to the client. */
  autoSend: AutoSendOutcome | null;
}

/**
 * Every live template, soonest due first, finished series included.
 *
 * Takes the same search and status the ordinary invoice list takes, because the
 * list screen keeps both controls on screen while this view is selected. A
 * toolbar that stays visible and silently stops working reads as a broken search
 * box, not as a view that ignores it.
 */
export const getRecurringInvoices = (opts: {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: string;
} = {}) => {
  const p = new URLSearchParams();
  p.set("page", String(opts.page ?? 1));
  p.set("pageSize", String(opts.pageSize ?? 25));
  if (opts.q) p.set("q", opts.q);
  if (opts.status) p.set("status", opts.status);
  return api.get<{ rows: RecurringInvoice[]; total: number; page: number; pageSize: number; pageCount: number }>(
    `/admin/crm/invoices/recurring?${p.toString()}`,
  );
};

/** What one template has produced, newest first. */
export const getRecurringChildren = (id: string) =>
  api.get<RecurringChild[]>(`/admin/crm/invoices/${id}/recurring/children`);

/** Set or clear the schedule. */
export const setInvoiceRecurring = (id: string, body: SchedulePut) =>
  api.put<RecurringInvoice>(`/admin/crm/invoices/${id}/recurring`, body);

/** Produce the next invoice now, without waiting for the schedule. */
export const runInvoiceRecurringNow = (id: string) =>
  api.post<RunNowResult>(`/admin/crm/invoices/${id}/recurring/run-now`, {});
